import * as semaphore from 'semaphore';
import { Injectable } from '@angular/core';
import { interval, merge, Subject } from 'rxjs';
import RegisteredAction from '../../../../model/registered-action';
import { ConnectionStatusService } from 'src/app/services/connection-status.service';
import { PlacerTaskChangesService } from '../offline-storage/placer-task-changes.service';
import {
  SubmitEventService,
  SubmitInProgressType
} from '../../submit-event.service';
import { SubmitLoadTaskService } from './controller-services/submit-load-task.service';
import { SubmitOtherTaskService } from './controller-services/submit-other-task.service';
import { SubmitRegisteredActionsService } from './controller-services/submit-registered-actions.service';
import { SnackService } from 'src/app/services/snack.service';
import { OfflineTaskChangesObject } from '../offline-storage/offline-task-changes-object';
import { ExplicitlyCompletableTask } from 'src/app/model/task-list/explicitly-completable-task';
import ErrorHelper from 'src/app/helpers/error-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import SemaphoreHelper from 'src/app/helpers/semaphore-helper';
import { NgForage } from 'ngforage';
import { PlacerTasksService } from 'src/app/services/placer-tasks.service';
import { TASK_TYPES } from 'src/app/model/task-list/task';

@Injectable({
  providedIn: 'root'
})
export class PlacerTaskSubmitService {
  private isSubmitting: boolean = false;
  private online: boolean = true;
  private readonly toSubmitLocalStorageKey = 'task-toSubmit';
  private readonly toSubmitGetSemaphore = semaphore(1);
  private readonly trySubmitInterval = 1000 * 10; // 10 seconds
  private connectionRestoredSubject = new Subject<any>();
  private registeredActionsSubmittedForTaskSubject = new Subject<
    SubmittedRegisteredActions
  >();
  private readonly toSubmitChange = new Subject<any>();

  constructor(
    private connectionStatusService: ConnectionStatusService,
    private placerTasksService: PlacerTasksService,
    private placerTaskChangesService: PlacerTaskChangesService,
    private submitEventService: SubmitEventService,
    private submitLoadTaskService: SubmitLoadTaskService,
    private submitOtherTaskService: SubmitOtherTaskService,
    private submitRegisteredActionsService: SubmitRegisteredActionsService,
    private snackService: SnackService,
    private ngForage: NgForage
  ) {
    this.connectionStatusService
      .getConnectionStatusSubject()
      .subscribe((online: boolean) => {
        const doSubmit = !this.online && online;
        this.online = online;
        if (doSubmit) {
          this.connectionRestoredSubject.next();
        }
      });
    merge(
      this.connectionRestoredSubject,
      interval(this.trySubmitInterval)
    ).subscribe(() => {
      this.doSubmit();
    });
  }

  public submitTask(
    taskChanges: OfflineTaskChangesObject,
    context: SubmitContext
  ): Promise<ExplicitlyCompletableTask> {
    if (this.online) {
      return this.doSubmitTask(taskChanges, context, true).catch(err => {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
          );
          this.addToSubmit(taskChanges.id, context, TaskSubmitType.FULL);
        }
        return Promise.reject(err);
      });
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.addToSubmit(taskChanges.id, context, TaskSubmitType.FULL);
      return Promise.reject({ status: 0 });
    }
  }

  public submitRegisteredActions(
    taskChanges: OfflineTaskChangesObject,
    context: SubmitContext
  ): Promise<RegisteredAction[]> {
    if (this.online) {
      return this.doSubmitRegisteredActions(taskChanges, context, true).catch(
        err => {
          if (ErrorHelper.isConnectionLostError(err)) {
            this.addToSubmit(
              taskChanges.id,
              context,
              TaskSubmitType.REGISTERED_ACTIONS
            );
          }
          return Promise.reject(err);
        }
      );
    } else {
      this.addToSubmit(
        taskChanges.id,
        context,
        TaskSubmitType.REGISTERED_ACTIONS
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async doSubmit(): Promise<void> {
    if (this.isSubmitting || !this.online) {
      return;
    }
    this.isSubmitting = true;
    try {
      const toSubmit = await this.getToSubmit();
      if (!CollectionHelper.isPopulated(toSubmit)) {
        return;
      }
      for (const submitEntry of toSubmit) {
        const taskChanges = await this.placerTaskChangesService.getObject(
          submitEntry.taskId
        );
        const isInProgress = await this.submitEventService.isInProgress(
          submitEntry.taskId.toString(),
          SubmitInProgressType.TASK
        );
        if (isInProgress) {
          continue;
        } else if (taskChanges == null) {
          await this.removeToSubmit(submitEntry.taskId);
        } else {
          try {
            const context = {
              employeeId: submitEntry.employeeId,
              dateString: submitEntry.dateString
            };
            submitEntry.submitType === TaskSubmitType.FULL
              ? await this.doSubmitTask(taskChanges, context, false)
              : await this.doSubmitRegisteredActions(
                  taskChanges,
                  context,
                  false
                );
          } catch (err) {
            if (!ErrorHelper.isConnectionLostError(err)) {
              this.snackService.showError(
                `Fout bij indienen taak ${submitEntry.taskId}: ${err}`
              );
            }
          }
        }
      }
    } catch (err) {
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  }

  public async retrySubmit(
    taskId: number,
    context: SubmitContext
  ): Promise<void> {
    if (!this.online) {
      this.snackService.showWarning(
        'Er is geen verbinding, gelieve opnieuw te proberen wanneer de verbinding hersteld is'
      );
      return;
    }
    const isInProgress = await this.submitEventService.isInProgress(
      taskId.toString(),
      SubmitInProgressType.TASK
    );
    if (isInProgress) {
      return;
    }
    const taskChanges = await this.placerTaskChangesService.getObject(taskId);
    if (taskChanges == null) {
      await this.removeToSubmit(taskId);
    } else {
      try {
        await this.doSubmitTask(taskChanges, context, false);
      } catch (err) {
        if (!ErrorHelper.isConnectionLostError(err)) {
          this.snackService.showError(
            `Fout bij indienen taak ${taskId}: ${err}`
          );
        }
      }
    }
  }

  private async doSubmitTask(
    taskChanges: OfflineTaskChangesObject,
    context: SubmitContext,
    immediateSubmit = false
  ): Promise<ExplicitlyCompletableTask> {
    await this.submitEventService.setSubmitInProgress(
      taskChanges.id.toString(),
      SubmitInProgressType.TASK
    );
    return new Promise(async (resolve, reject) => {
      try {
        const toReturn = await this.callSubmitTask(taskChanges);
        this.placerTasksService.storeTask(
          toReturn,
          context.employeeId,
          context.dateString
        );
        this.removeLocalChanges(taskChanges).then(() => resolve(toReturn));
        this.submitEventService.removeSubmitInProgress(
          taskChanges.id.toString(),
          SubmitInProgressType.TASK
        );
      } catch (err) {
        if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
          this.snackService.showError(err);
          this.removeLocalChanges(taskChanges).then(() => reject(err));
        } else {
          if (ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
            this.snackService.showWarning(
              'Server kon niet bereikt worden, project zal ingediend worden zodra internetverbinding hersteld is'
            );
          } else if (immediateSubmit) {
            this.snackService.showError(err);
          }
          reject(err);
        }
        this.submitEventService.removeSubmitInProgress(
          taskChanges.id.toString(),
          SubmitInProgressType.TASK
        );
      }
    });
  }

  private callSubmitTask(
    taskChanges: OfflineTaskChangesObject
  ): Promise<ExplicitlyCompletableTask> {
    switch (taskChanges.type) {
      case TASK_TYPES.LOAD_TASK:
        return this.submitLoadTaskService.submit(taskChanges);
      case TASK_TYPES.OTHER_TASK:
        return this.submitOtherTaskService.submit(taskChanges);
      default:
        throw new Error(`invalid type: ${taskChanges.type}`);
    }
  }

  private async doSubmitRegisteredActions(
    taskChanges: OfflineTaskChangesObject,
    context: SubmitContext,
    immediateSubmit = false
  ): Promise<RegisteredAction[]> {
    if (!CollectionHelper.isPopulated(taskChanges.registeredActions)) {
      throw new Error('Er zijn geen lokale geregistreerde acties');
    }

    return new Promise(async (resolve, reject) => {
      try {
        const toReturn = await this.submitRegisteredActionsService.submit(
          taskChanges
        );
        taskChanges.registeredActions.length = 0;
        await this.placerTaskChangesService.storeObject(taskChanges);
        if (CollectionHelper.isPopulated(toReturn)) {
          await this.placerTasksService.storeRegisteredActions(
            taskChanges.id,
            context.employeeId,
            context.dateString,
            toReturn
          );
        }
        await this.removeToSubmit(taskChanges.id);
        if (!immediateSubmit) {
          this.registeredActionsSubmittedForTaskSubject.next({
            taskId: taskChanges.id,
            registeredActions: toReturn
          });
        }
        resolve(toReturn);
      } catch (err) {
        if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
          this.snackService.showError(err);
          this.removeLocalChanges(taskChanges);
        }
        reject(err);
      }
    });
  }

  private async addToSubmit(
    id: number,
    context: SubmitContext,
    submitType: TaskSubmitType
  ): Promise<void> {
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      let toSave = await this.ngForage.getItem<TaskSubmitEntry[]>(
        this.toSubmitLocalStorageKey
      );
      toSave = toSave == null ? [] : toSave;
      const earlierItem = toSave.find(i => i.taskId === id);
      if (earlierItem == null) {
        toSave.push({
          taskId: id,
          submitType: submitType,
          employeeId: context.employeeId,
          dateString: context.dateString
        });
      } else if (earlierItem.submitType === TaskSubmitType.REGISTERED_ACTIONS) {
        earlierItem.submitType = submitType;
      }
      await this.ngForage.setItem(this.toSubmitLocalStorageKey, toSave);
      this.toSubmitChange.next();
    });
  }

  public async getToSubmit(): Promise<TaskSubmitEntry[]> {
    let toReturn: TaskSubmitEntry[];
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      toReturn = await this.ngForage.getItem<TaskSubmitEntry[]>(
        this.toSubmitLocalStorageKey
      );
    });
    return toReturn;
  }

  private async removeToSubmit(id: number): Promise<void> {
    if (id == null) {
      return;
    }
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      const saved = await this.ngForage.getItem<TaskSubmitEntry[]>(
        this.toSubmitLocalStorageKey
      );
      if (!CollectionHelper.isPopulated(saved)) {
        return;
      }
      CollectionHelper.filterInPlace(saved, e => e.taskId !== id);
      await this.ngForage.setItem(this.toSubmitLocalStorageKey, saved);
      this.toSubmitChange.next();
    });
  }

  private async removeLocalChanges(
    taskChanges: OfflineTaskChangesObject
  ): Promise<void> {
    await this.removeToSubmit(taskChanges.id);
    await this.placerTaskChangesService.removeObject(taskChanges);
  }

  public getRegisteredActionsSubmittedForTaskSubscribable(): Subject<any> {
    return this.registeredActionsSubmittedForTaskSubject;
  }

  public getToSubmitChangedSubscribable(): Subject<any> {
    return this.toSubmitChange;
  }
}

export interface SubmitContext {
  dateString: string;
  employeeId: number;
}

export enum TaskSubmitType {
  FULL,
  REGISTERED_ACTIONS
}

export interface TaskSubmitEntry {
  taskId: number;
  employeeId: number;
  dateString: string;
  submitType: TaskSubmitType;
}

export interface SubmittedRegisteredActions {
  taskId: number;
  registeredActions: RegisteredAction[];
}
